import React, { useState, useCallback } from 'react';
import { getWeeklyPriorities, generateWeeklyPriority } from '../../api/weekly';
import useApi from '../../hooks/useApi';
import TopBar from '../layout/TopBar';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import { WEEKLY_ITEM_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/dates';

function getWeekRange(weekStart) {
  if (!weekStart) return '';
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' });
  return `${fmt(start)} — ${fmt(end)}`;
}

function FocusItem({ item }) {
  const typeInfo = WEEKLY_ITEM_TYPES[item.type] || { label: item.type, color: '#6B7A99' };
  return (
    <div className="weekly-focus-item">
      <div className="weekly-focus-item__rank">{item.rank}</div>
      <div className="weekly-focus-item__content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span
            className="weekly-badge"
            style={{ background: typeInfo.color + '22', color: typeInfo.color, border: `1px solid ${typeInfo.color}44` }}
          >
            {typeInfo.label.toUpperCase()}
          </span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{item.title}</span>
          {item.estimated_effort && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 'auto' }}>{item.estimated_effort}</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: item.action ? 4 : 0 }}>{item.reason}</div>
        {item.action && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>→ {item.action}</div>
        )}
      </div>
    </div>
  );
}

function WeeklyCard({ wp }) {
  const weekRange = getWeekRange(wp.week_start);
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 className="card__title" style={{ margin: 0 }}>Semana del {weekRange}</h3>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Generado: {formatDateTime(wp.generated_at)}
        </span>
      </div>

      {wp.analysis_text && (
        <div className="weekly-analysis">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            📋 Análisis de la IA
          </div>
          <p style={{ fontSize: 14, margin: 0, lineHeight: 1.6 }}>{wp.analysis_text}</p>
        </div>
      )}

      {wp.focus_items?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            🎯 Enfócate en esto
          </div>
          {wp.focus_items.map((item, i) => (
            <FocusItem key={i} item={item} />
          ))}
        </div>
      )}

      {wp.deferrable?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            📌 Puede esperar
          </div>
          {wp.deferrable.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
              <span style={{ color: 'var(--text-muted)' }}>•</span>
              <div>
                <span>{item.title}</span>
                {item.reason && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}> — {item.reason}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeeklyView({ showToast }) {
  const [generating, setGenerating] = useState(false);
  const [page, setPage] = useState(1);

  const apiFn = useCallback(() => getWeeklyPriorities({ page, limit: 5 }), [page]);
  const { data, meta, loading, error, refetch } = useApi(apiFn, [page]);

  const currentWeek = data?.[0];
  const pastWeeks = data?.slice(1) || [];

  async function handleGenerate() {
    setGenerating(true);
    try {
      await generateWeeklyPriority();
      showToast?.('Generando priorización… disponible en unos segundos', 'info');
      setTimeout(() => { refetch(); setGenerating(false); }, 5000);
    } catch (err) {
      showToast?.(err.message || 'Error al generar', 'error');
      setGenerating(false);
    }
  }

  const currentWeekStart = (() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return monday.toISOString().slice(0, 10);
  })();

  const weekRange = getWeekRange(currentWeekStart);

  return (
    <div className="view-container">
      <TopBar title="Esta semana" subtitle={weekRange}>
        <button className="btn-secondary" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generando…' : '↻ Regenerar'}
        </button>
      </TopBar>

      <div className="view-content">
        {loading && <LoadingState rows={6} />}
        {error && <ErrorState message={error} onRetry={refetch} />}

        {!loading && !error && (
          !data?.length ? (
            <div className="empty-state">
              No hay priorización semanal todavía.
              <button className="btn-primary" style={{ marginTop: 12 }} onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generando…' : 'Generar ahora'}
              </button>
            </div>
          ) : (
            <>
              {currentWeek && <WeeklyCard wp={currentWeek} />}

              {pastWeeks.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12, fontWeight: 600 }}>
                    Semanas anteriores
                  </div>
                  {pastWeeks.map(wp => (
                    <WeeklyCard key={wp.id} wp={wp} />
                  ))}
                </div>
              )}

              {meta && meta.totalPages > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                  <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Más recientes</button>
                  <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                    {page} / {meta.totalPages}
                  </span>
                  <button className="btn-secondary" disabled={page >= meta.totalPages} onClick={() => setPage(p => p + 1)}>Anteriores →</button>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

export default WeeklyView;
